import rand from "@/utils/rand";
import {AwsService} from "@/simulation/AwsService";
import {Sqs} from "@/simulation/Sqs";
import {ExternalApi} from "@/simulation/ExternalApi";
import {EventBridge} from "@/simulation/EventBridge";
import wait from "@/utils/wait";

class Event {
    id = rand();
    promise: Promise<void>;
    errored = false;

    /**
     * @param {Function} behavior Must return a Promise
     */
    constructor(duration: number, behavior: Function, batchSize: number) {
        batchSize = batchSize ? batchSize : 1;

        // If we handle a batch, we need to repeat the duration + the behavior
        // (it has to take X times as long and have X more chances to fail :))
        this.promise = new Promise<void>((async function(this: Event, resolve: Function, reject: Function) {
            try {
                for (let i = 0; i < batchSize; i++) {
                    await wait(duration);
                    let promise = behavior();
                    promise = promise ? promise : Promise.resolve();
                    await promise;
                }
            } catch (e) {
                reject(e);
            }
            resolve();
        }).bind(this));

        // Update our error status on error
        this.promise.catch(() => {
            this.errored = true;
        });
    }
}

export class LambdaMaxConcurrencyReached {
}

export class Lambda implements AwsService {
    id = rand();
    name = 'Lambda';
    averageDuration = 800;
    durationVariation = 2;
    maxConcurrency = 1000;
    errorRate = 0;
    /**
     * Queue for async invocations.
     */
    queue: Sqs;
    /**
     * Events currently being synchronously processed.
     */
    events: Array<Event> = [];
    successes = 0;
    errors = 0;
    private connections: Array<AwsService> = [];
    onInvoke = () => {
        const rand = Math.floor(Math.random() * 100);
        if (this.errorRate > rand) {
            throw 'The Lambda failed';
        }
        // By default the lambda does not do anything on invocation
    };

    constructor() {
        this.queue = new Sqs();
        this.queue.retryCount = 2; // By default, Lambda retries async event 2 times
        this.queue.connectToLambda(this);
    }

    invoke(): void {
        this.invokeSync();
    }

    invokeSync(batchSize = 1): Promise<void> {
        if (this.events.length >= this.maxConcurrency) {
            throw new LambdaMaxConcurrencyReached;
        }

        const duration = rand(this.averageDuration / this.durationVariation, this.averageDuration * this.durationVariation);
        const event = new Event(duration, this.onInvoke, batchSize);
        this.events.push(event);
        event.promise.then(
            () => {
                this.successes++;
                this.eventFinished(event);
            },
            () => {
                this.errors++;
                // Make the error visible for a little while
                wait(300)
                    .then(() => this.eventFinished(event));
            },
        );
        return event.promise;
    }

    invokeAsync(): void {
        this.queue.invoke();
    }

    private eventFinished(event: Event) {
        const index = this.events.indexOf(event);
        if (index > -1) {
            this.events.splice(index, 1);
        }
    }

    connectToSqs(sqs: Sqs) {
        this.connections.push(sqs);
        this.onInvoke = () => sqs.invoke();
    }

    connectToEventBridge(eventBridge: EventBridge) {
        this.connections.push(eventBridge);
        this.onInvoke = () => eventBridge.invoke();
    }

    connectToApi(api: ExternalApi) {
        this.connections.push(api);
        this.onInvoke = () => api.call();
    }

    connectToDlq(dlq: Sqs) {
        this.queue.connectToDlq(dlq);
    }

    reset() {
        this.events = [];
        this.successes = 0;
        this.errors = 0;
        this.queue.reset();
    }

    outputs(): Array<AwsService> {
        let outputs = this.connections;
        if (this.queue.dlq) {
            outputs = outputs.concat([this.queue.dlq]);
        }
        return outputs;
    }
}
